import { CheckboxButtonGroup } from "@heroui-pro/react/checkbox-button-group";
import { RadioButtonGroup } from "@heroui-pro/react/radio-button-group";
import { memo, useState } from "react";
import { StepHeading } from "@funnel-ui/primitives/StepHeading";
import { ResponsiveAsset } from "@funnel-ui/primitives/ResponsiveAsset";

export type ImagePickerOption = {
  id: string;
  label: string;
  imageUrl: string;
  imageAlt?: string;
};

type ImagePickerProps = {
  question: string;
  description?: string;
  options: ImagePickerOption[];
  multi?: boolean;
  initialValue?: string | string[];
  columns?: 2 | 3;
  min?: number;
  max?: number;
  onChange?: (value: string | string[]) => void;
  className?: string;
  testId?: string;
};

const ImageItemContent = ({ option }: { option: ImagePickerOption }) => (
  <>
    <ResponsiveAsset
      src={option.imageUrl}
      alt={option.imageAlt ?? ""}
      loading="eager"
      className="aspect-3/4 w-full rounded-lg object-cover"
    />
    <span className="text-center text-sm font-medium">{option.label}</span>
  </>
);

type SingleGroupProps = {
  options: ImagePickerOption[];
  value: string | null;
  gridClass: string;
  name: string;
  ariaLabel: string;
  onChange: (next: string) => void;
};

const SingleGroup = ({
  options,
  value,
  gridClass,
  name,
  ariaLabel,
  onChange,
}: SingleGroupProps) => (
  <RadioButtonGroup
    aria-label={ariaLabel}
    className={gridClass}
    layout="grid"
    name={name}
    value={value}
    onChange={onChange}
  >
    {options.map((option) => (
      <RadioButtonGroup.Item
        key={option.id}
        value={option.id}
        className="flex-col gap-2 overflow-hidden p-2"
      >
        <RadioButtonGroup.ItemContent className="flex-col gap-2 p-0">
          <ImageItemContent option={option} />
        </RadioButtonGroup.ItemContent>
      </RadioButtonGroup.Item>
    ))}
  </RadioButtonGroup>
);

type MultiGroupProps = {
  options: ImagePickerOption[];
  value: string[];
  effectiveMax: number;
  gridClass: string;
  name: string;
  ariaLabel: string;
  onChange: (next: string[]) => void;
};

const MultiGroup = ({
  options,
  value,
  effectiveMax,
  gridClass,
  name,
  ariaLabel,
  onChange,
}: MultiGroupProps) => (
  <CheckboxButtonGroup
    aria-label={ariaLabel}
    className={gridClass}
    layout="grid"
    name={name}
    value={value}
    onChange={onChange}
  >
    {options.map((option) => {
      const atMax = value.length >= effectiveMax;
      const isSelected = value.includes(option.id);
      return (
        <CheckboxButtonGroup.Item
          key={option.id}
          value={option.id}
          isDisabled={atMax && !isSelected}
          className="flex-col gap-2 overflow-hidden p-2"
        >
          <CheckboxButtonGroup.ItemContent className="flex-col gap-2 p-0">
            <ImageItemContent option={option} />
          </CheckboxButtonGroup.ItemContent>
        </CheckboxButtonGroup.Item>
      );
    })}
  </CheckboxButtonGroup>
);

const ImagePickerImpl = ({
  question,
  description,
  options,
  multi = false,
  initialValue,
  columns = 2,
  min = 1,
  max,
  onChange,
  className,
  testId,
}: ImagePickerProps) => {
  const [singleValue, setSingleValue] = useState<string | undefined>(
    typeof initialValue === "string" ? initialValue : undefined,
  );
  const [multiValue, setMultiValue] = useState<string[]>(
    Array.isArray(initialValue) ? initialValue : [],
  );

  const effectiveMax = max ?? options.length;

  const handleSingle = (next: string): void => {
    setSingleValue(next);
    onChange?.(next);
  };
  const handleMulti = (next: string[]): void => {
    if (next.length > effectiveMax) return;
    setMultiValue(next);
    onChange?.(next);
  };

  const gridClass = columns === 3 ? "grid grid-cols-3 gap-3" : "grid grid-cols-2 gap-3";

  return (
    <section data-testid={testId} className={`flex flex-col gap-5 ${className ?? ""}`}>
      <StepHeading title={question} description={description} editablePath="question" />
      {multi ? (
        <MultiGroup
          options={options}
          value={multiValue}
          effectiveMax={effectiveMax}
          gridClass={gridClass}
          name={`image-picker-${testId ?? "default"}`}
          ariaLabel={question}
          onChange={handleMulti}
        />
      ) : (
        <SingleGroup
          options={options}
          value={singleValue ?? null}
          gridClass={gridClass}
          name={`image-picker-${testId ?? "default"}`}
          ariaLabel={question}
          onChange={handleSingle}
        />
      )}
      {multi && (
        <p
          role="status"
          aria-live="polite"
          className="text-xs"
          style={{ color: "hsl(var(--hero-fg-muted))" }}
        >
          {multiValue.length} of {effectiveMax} selected (min {min})
        </p>
      )}
    </section>
  );
};

export const ImagePicker = memo(ImagePickerImpl);

export default ImagePicker;
